import { useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import api from '../../../utils/functions/api';
import * as actions from '../../../redux/actions';

let _uid = Date.now();
const uid = () => ++_uid;

const EMPTY_ITEM = () => ({
  id: uid(), description: '', description_detail: '', quantity: 1, unit: 'JOB', unit_price: 0, amount: 0,
});

const EMPTY_FORM = () => ({
  document_number: '',
  date: dayjs().format('YYYY-MM-DD'),
  due_date: dayjs().format('YYYY-MM-DD'),
  customer_name: '',
  customer_address: '',
  customer_tax_id: '',
  topic: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  seller: 'SOTUS Engineering Co., Ltd.',
  customer_po_number: '',
  reference_text: 'Rev 01',
  notes: '',
  payment_terms: '',
  terms_conditions: '',
  po_document_url: '',
  credit_days: 30,
  items: [EMPTY_ITEM()],
  discount: 0,
  discountType: '%',
  vat_rate: 7,
  include_vat: false,
});

// สร้างเอกสาร QT/DN/BL/REC/INV จากโอกาสทางธุรกิจโดยตรง — ไม่มีโครงการจริงตอนนี้
// (ยังอยู่ช่วง marketing) เลยไม่มี ProjectSelector เหมือนหน้า Sale/DocumentDetail.js ปกติ
// ข้อมูลลูกค้า/ผู้ติดต่อ auto-fill จาก Contact ที่ผูกกับ opportunity นี้อยู่แล้ว
// (opportunity.contact_id บังคับมีเสมอตอนสร้างโอกาสทางธุรกิจ) แก้ไขต่อได้ก่อนบันทึก
export function useCreateSaleDocument({
  opportunity, documents = [], showSnack, onCreated,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [errors, setErrors] = useState({});
  const [previewingNumber, setPreviewingNumber] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPO, setUploadingPO] = useState(false);
  const poInputRef = useRef(null);

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const openDialog = useCallback((type) => {
    setDocType(type);
    setErrors({});
    setForm({
      ...EMPTY_FORM(),
      customer_name: opportunity.contact_name_snapshot || '',
      topic: opportunity.project_name || '',
    });
    setOpen(true);

    setPreviewingNumber(true);
    dispatch(actions.documentPreviewNumber(type))
      .then((num) => { if (num) setField('document_number', num); })
      .finally(() => setPreviewingNumber(false));

    // ดึงข้อมูลลูกค้าเต็มจาก Contact จริง — opportunity เก็บแค่ contact_id + name snapshot
    if (opportunity.contact_id) {
      dispatch(actions.contactGet(opportunity.contact_id)).then((c) => {
        if (!c) return;
        setForm((prev) => ({
          ...prev,
          customer_name: c.name || prev.customer_name,
          customer_address: [c.address1, c.address2, c.address3, c.zipcode].filter(Boolean).join(' '),
          customer_tax_id: c.taxId || '',
          contact_name: c.contact_name || '',
          contact_email: c.email || '',
          contact_phone: c.mobile || c.office_phone || '',
          credit_days: c.credit_days || 30,
        }));
      }).catch(() => {
        // ดึงไม่สำเร็จก็ยังกรอกเองได้ ไม่บล็อกการสร้างเอกสาร
      });
    }
  }, [dispatch, opportunity]);

  const closeDialog = () => setOpen(false);

  const handleItemsChange = useCallback((items) => setForm((p) => ({ ...p, items })), []);

  const handleUploadPO = async (file) => {
    if (!file) return;
    setUploadingPO(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/upload/file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setField('po_document_url', data?.url || '');
      setErrors((e) => ({ ...e, po_document_url: undefined }));
    } catch {
      showSnack('อัพโหลดไฟล์ไม่สำเร็จ — ไฟล์ต้องไม่เกิน 1MB', 'error');
    } finally {
      setUploadingPO(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.seller?.trim()) e.seller = 'กรุณาระบุผู้ขาย';
    if (docType === 'delivery_note' && !form.po_document_url) {
      e.po_document_url = 'กรุณาแนบใบ PO จากลูกค้าก่อนร่างใบส่งงาน';
    }
    if (!form.items || form.items.length === 0) e.items = 'กรุณาเพิ่มรายการ';
    const blankItem = form.items.some((it) => !it.description?.trim());
    if (blankItem) e.items = 'กรุณาระบุรายละเอียดทุกรายการ';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // เอกสารที่ไม่ใช่ใบเสนอราคา (DN/BL/REC/INV) ผูกกับเอกสารล่าสุดที่มีอยู่แล้วของ
      // opportunity นี้ (documents[0] มาจาก useOpportunityDocuments ที่ sort createdAt desc
      // อยู่แล้ว) เพื่อให้เข้า chain เดียวกัน (ดูกับ findGroupedByProject/ChainTimeline) —
      // ใบเสนอราคาเป็น root ของ chain เองเสมอ ไม่ผูกกับอะไรก่อนหน้า
      const parent = docType !== 'quotation' && documents.length > 0 ? documents[0] : null;
      const parentId = parent?._id || parent?.id;
      const root_quotation_id = parent
        ? (parent.document_type === 'quotation' ? parentId : (parent.root_quotation_id || parentId))
        : undefined;

      await dispatch(actions.documentCreate({
        document_number: form.document_number?.trim() || undefined,
        document_type: docType,
        date: form.date,
        due_date: form.due_date,
        client_id: opportunity.contact_id,
        opportunity_id: opportunity.id,
        parent_document_id: parentId,
        root_quotation_id,
        seller: form.seller,
        customer_po_number: form.customer_po_number,
        reference_text: form.reference_text,
        notes: form.notes,
        payment_terms: form.payment_terms,
        terms_conditions: form.terms_conditions,
        po_document_url: form.po_document_url,
        items: form.items.map(({ id: _id, ...it }) => it),
        discount: form.discount,
        discountType: form.discountType,
        vat_rate: form.vat_rate,
        include_vat: form.include_vat,
        snapshot_overrides: {
          project_name: form.topic,
          customer_name: form.customer_name,
          customer_address: form.customer_address,
          customer_tax_id: form.customer_tax_id,
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone,
          credit_days: form.credit_days,
        },
      }));
      showSnack('สร้างเอกสารสำเร็จ');
      setOpen(false);
      onCreated?.();
    } catch (err) {
      showSnack(err.message || 'สร้างเอกสารไม่สำเร็จ', 'error');
    } finally {
      setSaving(false);
    }
  };

  return {
    open,
    docType,
    openDialog,
    closeDialog,
    form,
    setField,
    errors,
    saving,
    previewingNumber,
    uploadingPO,
    poInputRef,
    handleUploadPO,
    handleItemsChange,
    submit,
  };
}

export default useCreateSaleDocument;
